import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

import { useTesterIdentity } from "./TesterIdentityProvider";
import BugList from "./BugList";
import BugThread from "./BugThread";
import ReportBugDialog from "./ReportBugDialog";
import QuestionList from "./QuestionList";
import QuestionView from "./QuestionView";
import AskQuestionDialog from "./AskQuestionDialog";
import FeedbackList from "./FeedbackList";
import SubmitFeedbackDialog from "./SubmitFeedbackDialog";
import ActiveHillBanner from "./ActiveHillBanner";
import { getActiveTestingHills } from "./api";

const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 560;
const MAXIMIZED_WIDTH = 480;

const FloatingPill = ({ onClick, label }) => (
  <Button
    variant="contained"
    color="primary"
    startIcon={<BugReportIcon />}
    onClick={onClick}
    sx={{
      position: "fixed",
      bottom: 16,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: (theme) => theme.zIndex.modal + 1,
      borderRadius: 999,
      boxShadow: 6
    }}
  >
    {label}
  </Button>
);

const PanelHeader = ({
  tester,
  onClose,
  onSignOut,
  isMaximized,
  onToggleMaximize
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      p: 1.5,
      borderBottom: 1,
      borderColor: "divider"
    }}
  >
    <BugReportIcon color="primary" />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">Tester Chat</Typography>
      {tester ? (
        <Typography variant="caption" color="textSecondary" noWrap>
          {tester.name || tester.email}
        </Typography>
      ) : null}
    </Box>
    {onSignOut ? (
      <Button size="small" onClick={onSignOut}>
        Sign out
      </Button>
    ) : null}
    <IconButton
      size="small"
      onClick={onToggleMaximize}
      aria-label={isMaximized ? "Collapse chat" : "Expand chat"}
      title={isMaximized ? "Collapse" : "Expand"}
    >
      {isMaximized ? (
        <CloseFullscreenIcon fontSize="small" />
      ) : (
        <OpenInFullIcon fontSize="small" />
      )}
    </IconButton>
    <IconButton size="small" onClick={onClose} aria-label="Close chat">
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

const SignInView = ({ onSignIn, error }) => (
  <Stack spacing={2} sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="body2" color="textSecondary">
      Sign in with your Aclymate-internal Google account to report bugs and
      chat with the fix agent.
    </Typography>
    <Button variant="contained" onClick={onSignIn}>
      Sign in with Google
    </Button>
    {error ? (
      <Typography variant="caption" color="error">
        {error}
      </Typography>
    ) : null}
  </Stack>
);

const NotATesterView = ({ user, onSignOut }) => (
  <Stack spacing={2} sx={{ p: 3, textAlign: "center" }}>
    <Typography variant="body2" color="textSecondary">
      {user?.email} isn&apos;t on the testers list yet.
    </Typography>
    <Typography variant="caption" color="textSecondary">
      Ask William to invite this email (the inviteTester admin endpoint
      creates a pending invite that auto-claims the next time you sign in).
    </Typography>
    <Button size="small" onClick={onSignOut}>
      Sign out and try a different account
    </Button>
  </Stack>
);

const TesterView = ({
  mode,
  onSetMode,
  onReportBug,
  onAskQuestion,
  onSendFeedback,
  selectedBugId,
  onSelectBug,
  selectedQuestionId,
  onSelectQuestion
}) => {
  if (selectedBugId) {
    return (
      <BugThread
        bugId={selectedBugId}
        onBack={() => onSelectBug(null)}
        onSelectBug={onSelectBug}
      />
    );
  }
  if (selectedQuestionId) {
    return (
      <QuestionView
        questionId={selectedQuestionId}
        onBack={() => onSelectQuestion(null)}
      />
    );
  }
  const modeButtons = {
    bugs: {
      icon: <AddCircleIcon />,
      label: "Report a bug",
      onClick: onReportBug
    },
    questions: {
      icon: <HelpOutlineIcon />,
      label: "Ask about the code",
      onClick: onAskQuestion
    },
    feedback: {
      icon: <FeedbackOutlinedIcon />,
      label: "Send feedback",
      onClick: onSendFeedback
    }
  };
  const modeLists = {
    bugs: <BugList selectedBugId={null} onSelect={onSelectBug} />,
    questions: (
      <QuestionList
        selectedQuestionId={null}
        onSelect={onSelectQuestion}
      />
    ),
    feedback: <FeedbackList />
  };
  const activeButton = modeButtons[mode] || modeButtons.bugs;
  const activeList = modeLists[mode] || modeLists.bugs;
  return (
    <Stack sx={{ height: "100%" }} spacing={0}>
      <Tabs
        value={mode}
        onChange={(_, next) => onSetMode(next)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        <Tab
          value="bugs"
          label="Bugs"
          icon={<BugReportIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 40, py: 0.5 }}
        />
        <Tab
          value="feedback"
          label="Feedback"
          icon={<FeedbackOutlinedIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 40, py: 0.5 }}
        />
        <Tab
          value="questions"
          label="Code Q&A"
          icon={<HelpOutlineIcon fontSize="small" />}
          iconPosition="start"
          sx={{ minHeight: 40, py: 0.5 }}
        />
      </Tabs>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Button
          variant="contained"
          startIcon={activeButton.icon}
          fullWidth
          onClick={activeButton.onClick}
        >
          {activeButton.label}
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>{activeList}</Box>
    </Stack>
  );
};

const ChatWidget = () => {
  const { palette } = useTheme();
  const {
    isConfigured,
    isLoading,
    testerUser,
    tester,
    isTester,
    getIdToken,
    signIn,
    signOut
  } = useTesterIdentity();
  const [open, setOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [mode, setMode] = useState("bugs");
  const [selectedBugId, setSelectedBugId] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [askDialogOpen, setAskDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [signInError, setSignInError] = useState(null);
  const [activeHill, setActiveHill] = useState(null);

  useEffect(() => {
    if (!isTester) {
      setActiveHill(null);
      return undefined;
    }
    let cancelled = false;
    getActiveTestingHills({ getIdToken })
      .then((result) => {
        if (cancelled) return;
        const hills = Array.isArray(result?.hills) ? result.hills : [];
        setActiveHill(hills[0] || null);
      })
      .catch(() => {
        if (cancelled) return;
        setActiveHill(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isTester, getIdToken]);

  if (!isConfigured) {
    return null;
  }

  const handleClose = () => {
    setOpen(false);
    setIsMaximized(false);
  };

  if (!open) {
    return <FloatingPill onClick={() => setOpen(true)} label="Tester chat" />;
  }

  const maximizedSx = {
    top: 0,
    right: 0,
    bottom: 0,
    width: MAXIMIZED_WIDTH,
    height: "100vh",
    maxWidth: "100vw",
    maxHeight: "100vh",
    borderRadius: 0
  };

  const compactSx = {
    bottom: 16,
    right: 16,
    width: PANEL_WIDTH,
    height: PANEL_HEIGHT,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)"
  };

  const handleSignIn = async () => {
    setSignInError(null);
    try {
      await signIn();
    } catch (err) {
      setSignInError(err.message || "Sign-in failed");
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            Loading…
          </Typography>
        </Box>
      );
    }
    if (!testerUser) {
      return <SignInView onSignIn={handleSignIn} error={signInError} />;
    }
    if (!isTester) {
      return <NotATesterView user={testerUser} onSignOut={signOut} />;
    }
    return (
      <TesterView
        mode={mode}
        onSetMode={(next) => {
          setMode(next);
          setSelectedBugId(null);
          setSelectedQuestionId(null);
        }}
        onReportBug={() => setReportDialogOpen(true)}
        onAskQuestion={() => setAskDialogOpen(true)}
        onSendFeedback={() => setFeedbackDialogOpen(true)}
        selectedBugId={selectedBugId}
        onSelectBug={setSelectedBugId}
        selectedQuestionId={selectedQuestionId}
        onSelectQuestion={setSelectedQuestionId}
      />
    );
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: palette.background.paper,
          ...(isMaximized ? maximizedSx : compactSx)
        }}
      >
        <PanelHeader
          tester={tester}
          onClose={handleClose}
          onSignOut={isTester ? signOut : null}
          isMaximized={isMaximized}
          onToggleMaximize={() => setIsMaximized((prev) => !prev)}
        />
        {isTester && activeHill ? <ActiveHillBanner hill={activeHill} /> : null}
        <Box sx={{ flex: 1, overflow: "hidden" }}>{renderBody()}</Box>
      </Paper>
      <ReportBugDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onReported={(bugId) => {
          setMode("bugs");
          setSelectedBugId(bugId);
        }}
        hillId={activeHill?.id || null}
      />
      <AskQuestionDialog
        open={askDialogOpen}
        onClose={() => setAskDialogOpen(false)}
        onAsked={(questionId) => {
          setMode("questions");
          setSelectedQuestionId(questionId);
        }}
      />
      <SubmitFeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        onSubmitted={() => setMode("feedback")}
        hillId={activeHill?.id || null}
      />
    </>
  );
};

export default ChatWidget;
